import type {
  CheckoutPaymentOptions,
  PlaceOrderInput,
  PlaceOrderResult,
  PublicOrderStatus,
  ShippingOption,
  Cart,
} from '@sshop/domain';

import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

import type { OrderService } from './OrderService';
import { cartDto } from '../cart/cart.dto';
import { toCart } from '../cart/cart.mapper';
import {
  checkoutPaymentOptionsResponseDto,
  placeOrderResponseDto,
  publicOrderStatusResponseDto,
  shippingOptionsResponseDto,
} from './order.dto';
import { toCheckoutPaymentOptions, toPlaceOrderResult, toShippingOption } from './order.mapper';

export function createLaravelOrderService(transport: Transport): OrderService {
  return {
    async quoteBuyNow(productId: string, quantity: number, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'GET',
        path: '/checkout/buy-now/quote',
        query: { product_id: productId, quantity },
        ...(signal !== undefined ? { signal } : {}),
      });
      return toCart(parseDto(cartDto, res.data, 'GET /checkout/buy-now/quote'));
    },
    async listShippingOptions(signal): Promise<readonly ShippingOption[]> {
      const res = await transport.request({
        method: 'GET',
        path: '/checkout/shipping-options',
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(
        shippingOptionsResponseDto,
        res.data,
        'GET /checkout/shipping-options',
      );
      return parsed.data.map(toShippingOption);
    },

    async placeOrder(input: PlaceOrderInput, signal): Promise<PlaceOrderResult> {
      const body = {
        customer_email: input.customerEmail,
        shipping_option_id: input.shippingOptionId,
        shipping_address: {
          recipient_name: input.shippingAddress.recipientName,
          phone: input.shippingAddress.phone,
          country: input.shippingAddress.country,
          city: input.shippingAddress.city,
          street: input.shippingAddress.street,
          apartment: input.shippingAddress.apartment,
          postal_code: input.shippingAddress.postalCode,
          notes: input.shippingAddress.notes,
          latitude: input.shippingAddress.latitude ?? null,
          longitude: input.shippingAddress.longitude ?? null,
        },
        billing_address: input.billingAddress,
        payment_method_kind: input.paymentMethodKind,
        save_card: input.saveCard,
        saved_payment_method_id: input.savedPaymentMethodId,
        installment: input.installment
          ? {
              discount_code: input.installment.discountCode,
              month: input.installment.month,
            }
          : null,
        coupon_code: input.couponCode,
        ...(input.buyNow ? {
          buy_now: { product_id: input.buyNow.productId, quantity: input.buyNow.quantity },
        } : {}),
        checkout_attempt_id: input.checkoutAttemptId,
        expected_total: input.expectedTotal,
      };
      const res = await transport.request({
        method: 'POST',
        path: '/checkout/orders',
        body,
        ...(signal !== undefined ? { signal } : {}),
      });
      return toPlaceOrderResult(parseDto(placeOrderResponseDto, res.data, 'POST /checkout/orders'));
    },

    async getPublicOrderStatus(id: string, token: string, signal): Promise<PublicOrderStatus> {
      const res = await transport.request({
        method: 'GET',
        path: `/checkout/orders/${encodeURIComponent(id)}/status?token=${encodeURIComponent(token)}`,
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(
        publicOrderStatusResponseDto,
        res.data,
        'GET /checkout/orders/status',
      );
      return {
        id: parsed.data.id,
        status: parsed.data.status,
        paymentMethod: parsed.data.payment_method,
        buyNow: parsed.data.buy_now
          ? { productId: parsed.data.buy_now.product_id, quantity: parsed.data.buy_now.quantity }
          : null,
        value: parsed.data.value,
        currency: parsed.data.currency,
        shipping: parsed.data.shipping,
        items: parsed.data.items.map((item) => ({
          itemId: item.item_id,
          itemName: item.item_name,
          price: item.price,
          quantity: item.quantity,
        })),
        updatedAt: parsed.data.updated_at,
      };
    },

    async listPaymentOptions(signal): Promise<CheckoutPaymentOptions> {
      const res = await transport.request({
        method: 'GET',
        path: '/checkout/payment-options',
        ...(signal !== undefined ? { signal } : {}),
      });
      return toCheckoutPaymentOptions(
        parseDto(checkoutPaymentOptionsResponseDto, res.data, 'GET /checkout/payment-options'),
      );
    },

    async removeSavedPaymentMethod(id, signal): Promise<void> {
      await transport.request({
        method: 'DELETE',
        path: `/checkout/saved-payment-methods/${encodeURIComponent(id)}`,
        ...(signal !== undefined ? { signal } : {}),
      });
    },
  };
}
